import React, { Component } from 'react'
import { connect } from 'react-redux'
import Modal from 'components/Luxkit/Modal/Modal'
import ShareByEmailForm from './ShareByEmailForm'
import ShareThis, { ShareOption } from 'components/Vendor/ShareThis'
import { copyValueToClipboard } from 'lib/clipboard'
import LineEnvelopeIcon from 'components/Luxkit/Icons/line/LineEnvelopeIcon'
import { offerShareButtonClick, offerShareComplete } from 'actions/OfferActions'
import { copyAvailable } from 'lib/clipboard'
import { isLoggedIn } from 'selectors/accountSelectors'
import LineLinkIcon from 'components/Luxkit/Icons/line/LineLinkIcon'

export const EMAIL_MODE = 'email'

interface ShareConfig {
  url?: string;
  customerPortalURL?: string;
  memberId?: string;
  brand?: string;
  holidaySynonym?: string;
  image?: App.Image;
  offerId?: string;
  offerLocation?: string;
  pathName?: string;
  region?: string;
  type?: string;
  utmCampaign?: string;
  shareTitle?: string;
  mailToSubject?: string;
  pageId?: string;
  pageType?: string;
  onSend?: (state: { friend_email: string }) => Promise<any>;
  shareMessage?: string;
  mailToObject?: string;
}

interface Props {
  loggedIn?: boolean;
  shareConfig: ShareConfig;
  children?: React.ReactElement;
  offerShareButtonClick: (...params: Parameters<typeof offerShareButtonClick>) => void;
  offerShareComplete: (...params: Parameters<typeof offerShareComplete>) => void;
  onShare?: (network: string) => void;
  mode?: typeof EMAIL_MODE;
}

interface State {
  isOpen: boolean;
  mode?: typeof EMAIL_MODE;
}

class ShareModal extends Component<React.PropsWithChildren<Props>, State> {
  state: State = {
    isOpen: false,
  }

  openShareModal = () => {
    const { offerShareButtonClick, shareConfig } = this.props
    if (shareConfig.pageId && shareConfig.pageType) {
      offerShareButtonClick(
        shareConfig.pageId,
        shareConfig.pageType,
      )
    }
    this.setState({ isOpen: true })
  }

  onShare = (shareMethod: string) => {
    const { offerShareComplete, shareConfig, onShare } = this.props
    if (shareConfig.pageId && shareConfig.pageType) {
      offerShareComplete(
        shareConfig.pageId,
        shareConfig.pageType,
        shareMethod,
      )
    }
    onShare?.(shareMethod)
    this.closeShareModal()
  }

  closeShareModal = () => {
    this.setState({ isOpen: false, mode: undefined })
  }

  constructMailTo = () => {
    if (this.props.shareConfig.url) {
      const body = encodeURIComponent(this.props.shareConfig.url)
      const subject = this.props.shareConfig.mailToSubject
      return `mailto:?subject=${subject}&body=${body}`
    }
  }

  copyToClipboard = () => {
    if (this.props.shareConfig.url) {
      copyValueToClipboard(this.props.shareConfig.url)
    }
    this.onShare('clipboard')
  }

  render() {
    const {
      shareConfig,
      loggedIn,
      children,
    } = this.props

    const {
      isOpen,
    } = this.state

    const shareMessage = shareConfig.shareMessage || ''
    const buttonChild = React.Children.only(children)
    const ShareButton = buttonChild ? React.cloneElement(buttonChild, {
      onClick: (e) => {
        if (buttonChild.props.onClick) {
          buttonChild.props.onClick(e)
        }
        this.openShareModal()
      },
    }) : null

    const mode = this.state.mode || this.props.mode

    const modalTitle = mode === EMAIL_MODE ? 'Share via email' : 'Share via'

    return (
      <>
        {ShareButton}
        <Modal
          title={modalTitle}
          isOpen={isOpen}
          onClose={this.closeShareModal}
        >
          {mode === EMAIL_MODE && (<ShareByEmailForm
            onShare={this.onShare}
            closeShareModal={this.closeShareModal}
            shareMessage={shareMessage}
            shareConfig={shareConfig}
          />)}
          {mode !== EMAIL_MODE && (
            <>
              <ShareOption
                kind="tertiary"
                href={loggedIn ? undefined : this.constructMailTo()}
                onClick={() => {
                  loggedIn ? this.setState({ mode: EMAIL_MODE }) : this.onShare('email')
                }}
                startIcon={<LineEnvelopeIcon />}
              >
                Email
              </ShareOption>
              <ShareThis
                onShare={this.onShare}
                shareConfig={shareConfig}
              />
              {copyAvailable() && (
                <ShareOption kind="tertiary" onClick={this.copyToClipboard} startIcon={<LineLinkIcon />}>
                  Copy link to clipboard
                </ShareOption>
              )}
            </>
          )}
        </Modal>
      </>
    )
  }
}

function mapStateToProps(state: App.State) {
  return {
    loggedIn: isLoggedIn(state),
  }
}

const actions = {
  offerShareButtonClick,
  offerShareComplete,
}

export default connect(mapStateToProps, actions)(ShareModal)
